import {
  PERFIL_FIELDS,
  applyCv,
  parseCv,
  type CvData,
  type ExpItem,
  type EduItem,
  type SkillItem,
} from "../cv-data";

const INPUT =
  "w-full rounded-md border border-line bg-bg px-3 py-2 font-mono text-sm text-ink focus:border-accent focus:outline-none";
const SMALL_BTN =
  "rounded border border-line px-2 py-1 font-mono text-[11px] text-mute hover:border-accent hover:text-accent";
const ADD_BTN =
  "rounded border border-accent/60 px-3 py-1.5 font-mono text-xs text-accent hover:bg-accent hover:text-bg";

const BLANK_EXP: ExpItem = { puesto: "", empresa: "", fechas: "", puntos: [""] };
const BLANK_EDU: EduItem = { titulo: "", centro: "", fechas: "" };
const BLANK_SKILL: SkillItem = { etiqueta: "", valor: "" };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-mono text-xs uppercase tracking-widest text-accent">▌ {title}</h2>
      {children}
    </section>
  );
}

// Full visual editor for the whole CV (personal data + every section, with
// add/remove of entries and bullets). Fully controlled by `source`: reads the
// `#let cv` block and writes edits back through `onChange`, never touching the
// body render. Rendered only while the source still matches a template.
export function CvForm({
  source,
  onChange,
}: {
  source: string;
  onChange: (nextSource: string) => void;
}) {
  const cv = parseCv(source);
  if (!cv) {
    return (
      <div className="p-5 font-mono text-sm text-mute">
        <span className="text-accent">[i]</span> Esta plantilla no tiene datos
        editables por formulario. Usa el modo «código».
      </div>
    );
  }
  const commit = (next: CvData) => onChange(applyCv(source, next));

  return (
    <div className="h-full overflow-y-auto p-5 space-y-7">
      <p className="font-mono text-[11px] leading-relaxed text-mute">
        Edita cada sección; la vista se actualiza sola. Añade o quita líneas con
        los botones. Nada sale de tu navegador.
      </p>

      {/* Datos personales */}
      <Section title="datos personales">
        <div className="grid grid-cols-1 gap-3">
          {PERFIL_FIELDS.map(({ key, label, placeholder }) => (
            <label key={key} className="block space-y-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mute">{label}</span>
              <input
                value={cv.perfil[key]}
                placeholder={placeholder}
                onChange={(e) => commit({ ...cv, perfil: { ...cv.perfil, [key]: e.target.value } })}
                className={INPUT}
              />
            </label>
          ))}
        </div>
      </Section>

      {/* Resumen / perfil */}
      <Section title="perfil (resumen)">
        <textarea
          value={cv.resumen}
          rows={4}
          placeholder="2–3 líneas con tu propuesta de valor y las keywords de la oferta."
          onChange={(e) => commit({ ...cv, resumen: e.target.value })}
          className={INPUT}
        />
      </Section>

      {/* Experiencia */}
      <Section title="experiencia">
        {cv.experiencia.map((exp, i) => (
          <div key={i} className="space-y-2 rounded-md border border-line bg-bg/30 p-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mute">
                puesto {i + 1}
              </span>
              <button
                type="button"
                className={SMALL_BTN}
                onClick={() =>
                  commit({ ...cv, experiencia: cv.experiencia.filter((_, k) => k !== i) })
                }
              >
                quitar
              </button>
            </div>
            {(["puesto", "empresa", "fechas"] as const).map((f) => (
              <input
                key={f}
                value={exp[f]}
                placeholder={f}
                onChange={(e) =>
                  commit({
                    ...cv,
                    experiencia: cv.experiencia.map((x, k) =>
                      k === i ? { ...x, [f]: e.target.value } : x,
                    ),
                  })
                }
                className={INPUT}
              />
            ))}
            <div className="space-y-1.5 pl-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mute">logros</span>
              {exp.puntos.map((p, pi) => (
                <div key={pi} className="flex gap-2">
                  <input
                    value={p}
                    placeholder="Logro cuantificado con verbo de acción…"
                    onChange={(e) =>
                      commit({
                        ...cv,
                        experiencia: cv.experiencia.map((x, k) =>
                          k === i
                            ? { ...x, puntos: x.puntos.map((q, qi) => (qi === pi ? e.target.value : q)) }
                            : x,
                        ),
                      })
                    }
                    className={INPUT}
                  />
                  <button
                    type="button"
                    className={SMALL_BTN}
                    onClick={() =>
                      commit({
                        ...cv,
                        experiencia: cv.experiencia.map((x, k) =>
                          k === i ? { ...x, puntos: x.puntos.filter((_, qi) => qi !== pi) } : x,
                        ),
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className={SMALL_BTN}
                onClick={() =>
                  commit({
                    ...cv,
                    experiencia: cv.experiencia.map((x, k) =>
                      k === i ? { ...x, puntos: [...x.puntos, ""] } : x,
                    ),
                  })
                }
              >
                + logro
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className={ADD_BTN}
          onClick={() => commit({ ...cv, experiencia: [...cv.experiencia, { ...BLANK_EXP }] })}
        >
          + experiencia
        </button>
      </Section>

      {/* Formación */}
      <Section title="formación">
        {cv.formacion.map((edu, i) => (
          <div key={i} className="space-y-2 rounded-md border border-line bg-bg/30 p-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mute">
                título {i + 1}
              </span>
              <button
                type="button"
                className={SMALL_BTN}
                onClick={() => commit({ ...cv, formacion: cv.formacion.filter((_, k) => k !== i) })}
              >
                quitar
              </button>
            </div>
            {(["titulo", "centro", "fechas"] as const).map((f) => (
              <input
                key={f}
                value={edu[f]}
                placeholder={f}
                onChange={(e) =>
                  commit({
                    ...cv,
                    formacion: cv.formacion.map((x, k) => (k === i ? { ...x, [f]: e.target.value } : x)),
                  })
                }
                className={INPUT}
              />
            ))}
          </div>
        ))}
        <button
          type="button"
          className={ADD_BTN}
          onClick={() => commit({ ...cv, formacion: [...cv.formacion, { ...BLANK_EDU }] })}
        >
          + formación
        </button>
      </Section>

      {/* Certificaciones */}
      <Section title="certificaciones">
        {cv.certificaciones.map((c, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={c}
              placeholder="OSCP — Offensive Security (2024)"
              onChange={(e) =>
                commit({
                  ...cv,
                  certificaciones: cv.certificaciones.map((x, k) => (k === i ? e.target.value : x)),
                })
              }
              className={INPUT}
            />
            <button
              type="button"
              className={SMALL_BTN}
              onClick={() =>
                commit({ ...cv, certificaciones: cv.certificaciones.filter((_, k) => k !== i) })
              }
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className={ADD_BTN}
          onClick={() => commit({ ...cv, certificaciones: [...cv.certificaciones, ""] })}
        >
          + certificación
        </button>
      </Section>

      {/* Habilidades */}
      <Section title="habilidades">
        {cv.habilidades.map((h, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={h.etiqueta}
              placeholder="categoría"
              onChange={(e) =>
                commit({
                  ...cv,
                  habilidades: cv.habilidades.map((x, k) =>
                    k === i ? { ...x, etiqueta: e.target.value } : x,
                  ),
                })
              }
              className={`${INPUT} max-w-[40%]`}
            />
            <input
              value={h.valor}
              placeholder="tecnologías / detalle"
              onChange={(e) =>
                commit({
                  ...cv,
                  habilidades: cv.habilidades.map((x, k) =>
                    k === i ? { ...x, valor: e.target.value } : x,
                  ),
                })
              }
              className={INPUT}
            />
            <button
              type="button"
              className={SMALL_BTN}
              onClick={() => commit({ ...cv, habilidades: cv.habilidades.filter((_, k) => k !== i) })}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className={ADD_BTN}
          onClick={() => commit({ ...cv, habilidades: [...cv.habilidades, { ...BLANK_SKILL }] })}
        >
          + habilidad
        </button>
      </Section>
    </div>
  );
}
